import useAutosizerScrollStatus from "hooks/useAutosizerScrollStatus";
import { VariableSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import { FilterItemCountsWithSelect } from "types";
import classNames from "classnames";
import { useRef } from "react";

import styles from "./style.module.scss";
import AnimatedBar from "./AnimatedSingleBar";

interface SingleCaptionProps {
  text: string;
}

const NoMatchingData = () => (
  <div className={styles.noMatchcontainer}>No Matching Data.</div>
);

const Row = ({ data, index, style }: any) => {
  const caption = data[index];
  const { title } = caption;

  const dynamicPaddingTop =
    index === 0 || index === data.length - 1 ? "1px" : "0";
  const singleBarRowStyle = {
    ...style,
    padding: `${dynamicPaddingTop} 16px 8px 16px`,
  };

  return (
    <div key={index} className={styles.singleBarRow} style={singleBarRowStyle}>
      <SingleCaption key={index} text={title} />
    </div>
  );
};

const SingleCaption = ({ text }: SingleCaptionProps) => {
  return (
    <AnimatedBar className={styles.caption}>
      <div className={styles.captionText}>{text}</div>
    </AnimatedBar>
  );
};

const CaptionContent = ({
  data,
  isContentSelected,
  showNoMatchingData,
}: CaptionContentProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLElement>(null);

  const { isAtBottom, isOverflowing } = useAutosizerScrollStatus(
    containerRef,
    listRef,
    { dependencyList: [data, isContentSelected] },
  );

  const captionWrapperClassnames = classNames(styles.captionsWrapper, {
    [styles.overflowingWrapper]: isOverflowing && !isAtBottom,
  });

  //handle dynamic content height
  const getItemSize = (index: number) => {
    const text = data[index].title;

    // Create the temporary elements
    const tempDiv = document.createElement("div");
    tempDiv.className = styles.caption;
    tempDiv.style.width = "308px"; //static
    tempDiv.style.visibility = "hidden"; // Ensure it does not affect the layout
    tempDiv.style.position = "absolute"; // Position off-screen

    const captionTextDiv = document.createElement("div");
    captionTextDiv.className = styles.captionText;
    captionTextDiv.textContent = text;

    tempDiv.appendChild(captionTextDiv);
    document.body.appendChild(tempDiv);

    const height = tempDiv.clientHeight + 8; // Adding padding bottom for gap

    // Clean up
    document.body.removeChild(tempDiv);

    // Return the calculated height
    return height || 58; // Fallback height
  };

  const dynamicWrapperHeight = () => {
    if (isContentSelected) {
      return { height: "100%", maxHeight: "100%" };
    }

    if ([0, 1, 2].includes(data.length)) {
      return { height: "126px" };
    } else {
      return { height: "184px" };
    }
  };

  return showNoMatchingData ? (
    <NoMatchingData />
  ) : (
    <div
      ref={containerRef}
      className={captionWrapperClassnames}
      style={dynamicWrapperHeight()}
    >
      <AutoSizer>
        {({ height, width }: any) => (
          <List
            innerRef={listRef}
            outerRef={containerRef}
            height={height}
            width={width}
            itemData={data}
            itemSize={getItemSize}
            itemCount={data.length}
            className={`List ${styles.barListContainer}`}
          >
            {({ index, style }) => {
              return (
                <Row key={index} data={data} index={index} style={style} />
              );
            }}
          </List>
        )}
      </AutoSizer>
    </div>
  );
};

export default CaptionContent;

interface CaptionContentProps {
  isContentSelected: any;
  showNoMatchingData: any;
  data: FilterItemCountsWithSelect[];
}
